import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CATEGORY, elapsedOpen, errMsg, fileUrl, fmtDate, STATUS } from "@/lib/api";
import { PriorityBadge, Spinner, StatusBadge } from "@/components/bits";
import TicketChat from "@/components/TicketChat";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Clock, History, ImageIcon, User } from "lucide-react";
import { toast } from "sonner";

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [team, setTeam] = useState([]);

  const load = async () => {
    try {
      const [{ data: t }, { data: m }, { data: h }, { data: teamUsers }] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get(`/tickets/${id}/messages`),
        api.get(`/tickets/${id}/history`),
        api.get("/tenant/users"),
      ]);
      setTicket(t);
      setMessages(m);
      setHistory(h);
      setTeam(teamUsers.filter((u) => ["tenant_admin", "agent"].includes(u.role) && u.active));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { load(); }, [id]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, body);
      setTicket(data);
      const { data: h } = await api.get(`/tickets/${id}/history`);
      setHistory(h);
      toast.success("Chamado atualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!ticket) return <Spinner />;

  return (
    <div data-testid="ticket-detail-page">
      <Link to="/app/chamados" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 mb-4 transition-colors duration-200">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar para chamados
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs text-zinc-500 mb-1">#{String(ticket.number).padStart(4, "0")} · {CATEGORY[ticket.category]} · origem {ticket.source === "api" ? "API" : "Portal"}{ticket.area_name && <span className="font-sans" data-testid="ticket-area"> · Local: <span className="text-zinc-300">{ticket.area_name}</span></span>}</p>
          <h1 className="text-2xl tracking-tight font-semibold text-zinc-100" data-testid="ticket-title">{ticket.title}</h1>
          <p className="text-sm text-zinc-400 mt-1 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {ticket.client_name} · aberto em {fmtDate(ticket.created_at)}</p>
          {(ticket.origin_name || ticket.external_ref) && (
            <p className="text-sm text-zinc-400 mt-1" data-testid="ticket-origin-info">
              Nome do cliente: <span className="text-zinc-200 font-medium">{ticket.origin_name}</span>
              {ticket.external_ref && <> · Origem: <span className="text-zinc-200 font-medium">{ticket.external_ref}</span></>}
            </p>
          )}
        </div>
        <PriorityBadge value={ticket.priority} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
            <h2 className="text-sm font-medium text-zinc-100 mb-3">Descrição</h2>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
            {ticket.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4">
                {ticket.attachments.map((p) => (
                  <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid="ticket-attachment">
                    <img src={fileUrl(p)} alt="anexo" className="h-20 w-20 object-cover rounded-md border border-zinc-800" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
            <h2 className="text-sm font-medium text-zinc-100 mb-4 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-indigo-400" /> Conversa com o cliente</h2>
            <TicketChat ticketId={id} messages={messages} currentUser={user}
              onNew={(m) => setMessages([...messages, m])} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 space-y-4">
            <h2 className="text-sm font-medium text-zinc-100">Gerenciamento</h2>
            <div>
              <p className="text-xs text-zinc-500 mb-1.5">Status</p>
              <Select value={ticket.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="ticket-status-select" className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                  {Object.entries(STATUS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`status-option-${v}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1.5">Atendente responsável</p>
              <Select value={ticket.assigned_agent_id || "none"} onValueChange={(v) => patch({ assigned_agent_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ticket-agent-select" className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                  <SelectItem value="none">Não atribuído</SelectItem>
                  {team.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between text-xs text-zinc-500 pt-1">
              <span>Status atual</span>
              <StatusBadge value={ticket.status} />
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-zinc-100 flex items-center gap-2"><History className="h-4 w-4 text-indigo-400" /> Linha do tempo</h2>
              <span data-testid="ticket-open-elapsed" title={["resolvido", "fechado"].includes(ticket.status) ? "Tempo total entre abertura e última atualização de status" : "Tempo desde a abertura até agora"}
                className="flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-400/10 rounded-full px-2.5 py-1">
                <Clock className="h-3.5 w-3.5" />
                {["resolvido", "fechado"].includes(ticket.status)
                  ? `Durou ${elapsedOpen(ticket.created_at, history[history.length - 1]?.created_at)}`
                  : `Aberto há ${elapsedOpen(ticket.created_at)}`}
              </span>
            </div>
            <div className="relative pl-5 space-y-5 before:absolute before:left-[5px] before:top-1 before:bottom-1 before:w-px before:bg-zinc-800" data-testid="ticket-timeline">
              {history.map((h) => (
                <div key={h.id} className="relative">
                  <span className="absolute -left-5 top-1 h-2.5 w-2.5 rounded-full bg-indigo-500 ring-4 ring-indigo-500/10" />
                  <p className="text-xs text-zinc-300">
                    {h.previous_status ? <>{STATUS[h.previous_status]} → <span className="text-zinc-100 font-medium">{STATUS[h.new_status]}</span></> : <>Chamado aberto</>}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{h.changed_by_name} · {fmtDate(h.created_at)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
